import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Users, DollarSign, Clock, Mail, Phone, Star,
  Eye, Edit2, CheckCircle, X, Instagram, Facebook, Linkedin, Twitter, Globe,
  MessageSquare, PhoneCall, MapPin, Plus, Trash2, Save, Tag, Package
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import StarRating from '../components/StarRating';

export default function ReferidorDetail() {
  const { referidorId } = useParams();
  const navigate = useNavigate();
  const [referidor, setReferidor] = useState(null);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leads');
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [commissionForm, setCommissionForm] = useState({ amount: 0, status: 'pending' });
  
  // New state for toques, profile tag, and services
  const [services, setServices] = useState([]);
  const [profileTag, setProfileTag] = useState('');
  const [savingTag, setSavingTag] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [savingServices, setSavingServices] = useState(false);
  const [showToqueModal, setShowToqueModal] = useState(false);
  const [editingToque, setEditingToque] = useState(null); // For editing existing toque
  const [toqueForm, setToqueForm] = useState({ channel: 'whatsapp', customChannel: '', comment: '', status: 'por_contactar', attitude: 3 });
  const [savingToque, setSavingToque] = useState(false);

  // Attitude emojis for toques
  const attitudeEmojis = [
    { value: 1, emoji: '😞', label: 'Muy negativo', color: 'bg-red-100' },
    { value: 2, emoji: '😕', label: 'Poco interesado', color: 'bg-orange-100' },
    { value: 3, emoji: '😐', label: 'Neutral', color: 'bg-slate-100' },
    { value: 4, emoji: '🙂', label: 'Interesado', color: 'bg-green-100' },
    { value: 5, emoji: '😄', label: 'Muy positivo', color: 'bg-emerald-100' }
  ];

  const getAttitudeEmoji = (value) => {
    const attitude = attitudeEmojis.find(a => a.value === value);
    return attitude || attitudeEmojis[2]; // Default to neutral
  };

  // Calculate days since last toque
  const getDaysSinceLastToque = () => {
    const toques = referidor?.toques || [];
    if (toques.length === 0) return null;
    
    const sortedToques = [...toques].sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );
    const lastToque = sortedToques[0];
    if (!lastToque?.createdAt) return null;
    
    const lastDate = new Date(lastToque.createdAt);
    const today = new Date();
    const diffTime = Math.abs(today - lastDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [data, servicesData] = await Promise.all([
        api.getReferidorDetail(referidorId),
        api.getServices()
      ]);
      setReferidor(data.referidor);
      setLeads(data.leads);
      setStats(data.stats);
      setServices(servicesData || []);
      setProfileTag(data.referidor?.profileTag || '');
      setSelectedServices(data.referidor?.optimalServices || []);
    } catch (error) {
      toast.error('Error al cargar datos del referidor');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [referidorId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRatingUpdate = async (rating) => {
    try {
      await api.updateReferidorRating(referidorId, rating);
      setReferidor(prev => ({ ...prev, referidorRating: rating }));
      toast.success('Calificación actualizada');
    } catch (error) {
      toast.error('Error al actualizar calificación');
    }
  };

  // Profile Tag handlers
  const handleSaveProfileTag = async () => {
    setSavingTag(true);
    try {
      await api.updateProfileTag(referidorId, profileTag);
      setReferidor(prev => ({ ...prev, profileTag }));
      toast.success('Tag de perfil actualizado');
    } catch (error) {
      toast.error('Error al guardar tag');
    } finally {
      setSavingTag(false);
    }
  };

  // Optimal Services handlers
  const handleToggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSaveServices = async () => {
    setSavingServices(true);
    try {
      await api.updateOptimalServices(referidorId, selectedServices);
      setReferidor(prev => ({ ...prev, optimalServices: selectedServices }));
      toast.success('Servicios actualizados');
    } catch (error) {
      toast.error('Error al guardar servicios');
    } finally {
      setSavingServices(false);
    }
  };

  // Toques handlers
  const handleAddToque = async () => {
    if (!toqueForm.channel) return;
    if (toqueForm.channel === 'otro' && !toqueForm.customChannel.trim()) {
      toast.error('Por favor especifica el canal');
      return;
    }
    setSavingToque(true);
    try {
      const result = await api.addToque(referidorId, toqueForm);
      setReferidor(prev => ({
        ...prev,
        toques: [...(prev.toques || []), result.toque]
      }));
      closeToqueModal();
      toast.success('Toque agregado');
    } catch (error) {
      toast.error('Error al agregar toque');
    } finally {
      setSavingToque(false);
    }
  };

  const handleEditToque = async () => {
    if (!editingToque?.id) return;
    if (toqueForm.channel === 'otro' && !toqueForm.customChannel.trim()) {
      toast.error('Por favor especifica el canal');
      return;
    }
    setSavingToque(true);
    try {
      const result = await api.updateToque(referidorId, editingToque.id, toqueForm);
      setReferidor(prev => ({
        ...prev,
        toques: (prev.toques || []).map(t => 
          t.id === editingToque.id ? result.toque : t
        )
      }));
      closeToqueModal();
      toast.success('Toque actualizado');
    } catch (error) {
      toast.error('Error al actualizar toque');
    } finally {
      setSavingToque(false);
    }
  };

  const openEditToque = (toque) => {
    setEditingToque(toque);
    setToqueForm({
      channel: toque.channel,
      customChannel: toque.customChannel || '',
      comment: toque.comment || '',
      status: toque.status,
      attitude: toque.attitude || 3
    });
    setShowToqueModal(true);
  };

  const closeToqueModal = () => {
    setShowToqueModal(false);
    setEditingToque(null);
    setToqueForm({ channel: 'whatsapp', customChannel: '', comment: '', status: 'por_contactar', attitude: 3 });
  };

  const handleDeleteToque = async (toqueId) => {
    if (!window.confirm('¿Eliminar este toque?')) return;
    try {
      await api.deleteToque(referidorId, toqueId);
      setReferidor(prev => ({
        ...prev,
        toques: (prev.toques || []).filter(t => t.id !== toqueId)
      }));
      toast.success('Toque eliminado');
    } catch (error) {
      toast.error('Error al eliminar toque');
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'whatsapp': return <MessageSquare className="w-4 h-4 text-green-600" />;
      case 'linkedin': return <Linkedin className="w-4 h-4 text-blue-600" />;
      case 'llamada': return <PhoneCall className="w-4 h-4 text-indigo-600" />;
      case 'presencial': return <MapPin className="w-4 h-4 text-purple-600" />;
      default: return <MessageSquare className="w-4 h-4 text-slate-500" />;
    }
  };

  const getChannelLabel = (channel, customChannel = '') => {
    const labels = {
      whatsapp: 'WhatsApp',
      linkedin: 'LinkedIn',
      llamada: 'Llamada',
      presencial: 'Presencial',
      otro: customChannel || 'Otro'
    };
    return labels[channel] || channel;
  };

  const handleUpdateCommission = async () => {
    if (!selectedLead) return;
    try {
      await api.updateLeadCommission(selectedLead.id, commissionForm.amount, commissionForm.status);
      toast.success('Comisión actualizada');
      setShowCommissionModal(false);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar comisión');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency', currency: 'COP', minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'paid': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'approved': return 'Aprobada';
      case 'paid': return 'Pagada';
      case 'rejected': return 'Rechazada';
      default: return 'Sin estado';
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!referidor) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">Referidor no encontrado</p>
        <button onClick={() => navigate('/referidos')} className="text-indigo-600 hover:underline mt-2">
          Volver a Referidos
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="referidor-detail">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button 
          onClick={() => navigate('/referidos')} 
          className="p-2 hover:bg-slate-100 rounded-lg mt-1"
          data-testid="back-to-referidos"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-lg font-bold text-indigo-600">
                {referidor.name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="referidor-name">
                {referidor.name}
              </h1>
              <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" /> {referidor.email}
                </span>
                {referidor.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" /> {referidor.phone}
                  </span>
                )}
              </div>
              {/* Additional Contact Info */}
              {(referidor.phones?.length > 0 || referidor.emails?.length > 0) && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {referidor.phones?.map((phone, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                      <Phone className="w-3 h-3" /> {phone}
                    </span>
                  ))}
                  {referidor.emails?.map((email, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                      <Mail className="w-3 h-3" /> {email}
                    </span>
                  ))}
                </div>
              )}
              {/* Social Media */}
              {referidor.socialMedia && Object.keys(referidor.socialMedia).some(k => referidor.socialMedia[k]) && (
                <div className="mt-2 flex items-center gap-2">
                  {referidor.socialMedia?.instagram && (
                    <a href={`https://instagram.com/${referidor.socialMedia.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-pink-100 text-pink-600 rounded-lg hover:bg-pink-200 transition-colors">
                      <Instagram className="w-4 h-4" />
                    </a>
                  )}
                  {referidor.socialMedia?.facebook && (
                    <a href={referidor.socialMedia.facebook.startsWith('http') ? referidor.socialMedia.facebook : `https://facebook.com/${referidor.socialMedia.facebook}`} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors">
                      <Facebook className="w-4 h-4" />
                    </a>
                  )}
                  {referidor.socialMedia?.linkedin && (
                    <a href={referidor.socialMedia.linkedin.startsWith('http') ? referidor.socialMedia.linkedin : `https://linkedin.com/in/${referidor.socialMedia.linkedin}`} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-sky-100 text-sky-600 rounded-lg hover:bg-sky-200 transition-colors">
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                  {referidor.socialMedia?.twitter && (
                    <a href={`https://twitter.com/${referidor.socialMedia.twitter.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors">
                      <Twitter className="w-4 h-4" />
                    </a>
                  )}
                  {referidor.socialMedia?.website && (
                    <a href={referidor.socialMedia.website.startsWith('http') ? referidor.socialMedia.website : `https://${referidor.socialMedia.website}`} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors">
                      <Globe className="w-4 h-4" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span className="text-sm text-slate-500">Calidad:</span>
            <StarRating 
              rating={referidor.referidorRating || 0} 
              onRate={handleRatingUpdate}
              size="md"
            />
          </div>
          {/* Profile Tag Display */}
          {referidor.profileTag && (
            <div className="mt-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium">
                <Tag className="w-3.5 h-3.5" />
                {referidor.profileTag}
              </span>
            </div>
          )}
          {/* Optimal Services Display */}
          {referidor.optimalServices?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="text-xs text-slate-500">Óptimo para:</span>
              {referidor.optimalServices.map(sId => {
                const service = services.find(s => s.id === sId);
                return service ? (
                  <span key={sId} className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full">
                    <Package className="w-3 h-3" />
                    {service.name}
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Leads</p>
              <p className="text-2xl font-bold text-slate-900" data-testid="stat-total-leads">{stats.totalLeads || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Comisiones</p>
              <p className="text-2xl font-bold text-indigo-600">{formatCurrency(stats.totalCommission)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Comisiones Pagadas</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.paidCommission)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Comisiones Pendientes</p>
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(stats.pendingCommission)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-4 flex-wrap">
          <button
            onClick={() => setActiveTab('leads')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'leads' ? 'text-indigo-600 border-indigo-600' : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" /> Leads ({leads.length})
          </button>
          <button
            onClick={() => setActiveTab('commissions')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'commissions' ? 'text-indigo-600 border-indigo-600' : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            <DollarSign className="w-4 h-4 inline mr-2" /> Comisiones
          </button>
          <button
            onClick={() => setActiveTab('toques')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'toques' ? 'text-indigo-600 border-indigo-600' : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-2" /> Toques ({referidor?.toques?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'profile' ? 'text-indigo-600 border-indigo-600' : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            <Tag className="w-4 h-4 inline mr-2" /> Perfil
          </button>
        </nav>
      </div>

      {/* Leads Tab */}
      {activeTab === 'leads' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {leads.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Este referidor no tiene leads asociados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Lead</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Contacto</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Embudo</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Calificación</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Comisión</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Estado</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Fecha</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{lead.name}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          <p className="text-sm text-slate-700 flex items-center gap-1">
                            <Mail className="w-3 h-3 text-slate-400" /> {lead.email}
                          </p>
                          {lead.phone && (
                            <p className="text-sm text-slate-700 flex items-center gap-1">
                              <Phone className="w-3 h-3 text-slate-400" /> {lead.phone}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: lead.stageColor || '#6366f1' }}></span>
                          <div>
                            <p className="text-sm text-slate-700">{lead.pipelineName || '-'}</p>
                            <p className="text-xs text-slate-500">{lead.stageName || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <StarRating rating={lead.rating || 0} onRate={() => {}} size="sm" />
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-900">{formatCurrency(lead.commissionAmount)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(lead.commissionStatus)}`}>
                          {getStatusLabel(lead.commissionStatus)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-500">{formatDate(lead.createdAt)}</p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/lead/${lead.id}`)}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Ver lead"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedLead(lead);
                              setCommissionForm({ amount: lead.commissionAmount || 0, status: lead.commissionStatus || 'pending' });
                              setShowCommissionModal(true);
                            }}
                            className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                            title="Editar comisión"
                          >
                            <DollarSign className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Commissions Tab */}
      {activeTab === 'commissions' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Lead</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Monto</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Estado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Fecha</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center">
                      <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No hay comisiones</p>
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{lead.name}</p>
                        <p className="text-xs text-slate-500">{lead.email}</p>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                        {formatCurrency(lead.commissionAmount)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(lead.commissionStatus)}`}>
                          {getStatusLabel(lead.commissionStatus)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">{formatDate(lead.createdAt)}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => {
                            setSelectedLead(lead);
                            setCommissionForm({ amount: lead.commissionAmount || 0, status: lead.commissionStatus || 'pending' });
                            setShowCommissionModal(true);
                          }}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Toques Tab */}
      {activeTab === 'toques' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-5 border-b border-slate-200 flex flex-wrap justify-between items-center gap-4">
            <div>
              <h2 className="font-semibold text-slate-900">Historial de Toques</h2>
              <p className="text-sm text-slate-500">Seguimiento de contactos con el referidor</p>
            </div>
            <div className="flex items-center gap-4">
              {/* Days since last toque */}
              {getDaysSinceLastToque() !== null && (
                <div className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  getDaysSinceLastToque() > 30 ? 'bg-red-100 text-red-700' :
                  getDaysSinceLastToque() > 14 ? 'bg-amber-100 text-amber-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  <Clock className="w-4 h-4 inline mr-1.5" />
                  {getDaysSinceLastToque() === 0 ? 'Hoy' :
                   getDaysSinceLastToque() === 1 ? 'Hace 1 día' :
                   `Hace ${getDaysSinceLastToque()} días`}
                </div>
              )}
              <Button onClick={() => { setEditingToque(null); setShowToqueModal(true); }} className="gap-2">
                <Plus className="w-4 h-4" /> Agregar Toque
              </Button>
            </div>
          </div>
          <div className="p-5">
            {(!referidor.toques || referidor.toques.length === 0) ? (
              <div className="text-center py-8 text-slate-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay toques registrados</p>
                <p className="text-sm">Agrega un toque para comenzar el seguimiento</p>
              </div>
            ) : (
              <div className="space-y-3">
                {[...referidor.toques].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((toque, idx) => {
                  const attitude = getAttitudeEmoji(toque.attitude);
                  return (
                  <div key={toque.id || idx} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                    <div className={`p-2 rounded-lg ${
                      toque.status === 'contactado' ? 'bg-green-100' : 'bg-amber-100'
                    }`}>
                      {getChannelIcon(toque.channel)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">{getChannelLabel(toque.channel, toque.customChannel)}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          toque.status === 'contactado' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {toque.status === 'contactado' ? 'Contactado' : 'Por Contactar'}
                        </span>
                        {/* Attitude Emoji */}
                        <span 
                          className={`text-lg px-2 py-0.5 rounded-full ${attitude.color}`}
                          title={attitude.label}
                        >
                          {attitude.emoji}
                        </span>
                      </div>
                      {toque.comment && (
                        <p className="text-sm text-slate-600 mt-1">{toque.comment}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                        <span>{formatDate(toque.createdAt)}</span>
                        {toque.createdByName && (
                          <>
                            <span>•</span>
                            <span>Por: {toque.createdByName}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEditToque(toque)}
                        className="p-1.5 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Editar toque"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteToque(toque.id)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar toque"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Profile Tag */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-5 border-b border-slate-200">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Tag className="w-4 h-4 text-indigo-500" /> Tag de Perfil
              </h2>
              <p className="text-sm text-slate-500 mt-1">Descripción corta del referidor</p>
            </div>
            <div className="p-5 space-y-4">
              <Input
                placeholder="Ej: Experto en Marketing Digital, Aliado Estratégico..."
                value={profileTag}
                onChange={(e) => setProfileTag(e.target.value)}
              />
              <Button 
                onClick={handleSaveProfileTag} 
                disabled={savingTag}
                className="w-full gap-2"
              >
                {savingTag ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar Tag
              </Button>
            </div>
          </div>

          {/* Optimal Services */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-5 border-b border-slate-200">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Package className="w-4 h-4 text-green-500" /> Servicios Óptimos
              </h2>
              <p className="text-sm text-slate-500 mt-1">Servicios que este aliado puede ofrecer</p>
            </div>
            <div className="p-5 space-y-4">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {services.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No hay servicios disponibles</p>
                ) : (
                  services.map(service => (
                    <label
                      key={service.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedServices.includes(service.id) 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-slate-50 border border-transparent hover:bg-slate-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedServices.includes(service.id)}
                        onChange={() => handleToggleService(service.id)}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 text-sm truncate">{service.name}</p>
                        {service.description && (
                          <p className="text-xs text-slate-500 truncate">{service.description}</p>
                        )}
                      </div>
                    </label>
                  ))
                )}
              </div>
              <Button 
                onClick={handleSaveServices} 
                disabled={savingServices}
                className="w-full gap-2"
              >
                {savingServices ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar Servicios ({selectedServices.length} seleccionados)
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Toque Modal */}
      {showToqueModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-xl">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingToque ? 'Editar Toque' : 'Agregar Toque'}
              </h3>
              <button onClick={closeToqueModal} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Canal de Contacto</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare, color: 'green' },
                    { value: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: 'blue' },
                    { value: 'llamada', label: 'Llamada', icon: PhoneCall, color: 'indigo' },
                    { value: 'presencial', label: 'Presencial', icon: MapPin, color: 'purple' },
                    { value: 'otro', label: 'Otro', icon: MessageSquare, color: 'slate' }
                  ].map(channel => {
                    const Icon = channel.icon;
                    return (
                      <button
                        key={channel.value}
                        type="button"
                        onClick={() => setToqueForm({ ...toqueForm, channel: channel.value, customChannel: channel.value !== 'otro' ? '' : toqueForm.customChannel })}
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                          toqueForm.channel === channel.value 
                            ? `border-${channel.color}-500 bg-${channel.color}-50` 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Icon className={`w-4 h-4 text-${channel.color}-600`} />
                        <span className="text-sm font-medium">{channel.label}</span>
                      </button>
                    );
                  })}
                </div>
                {/* Custom channel input when "Otro" is selected */}
                {toqueForm.channel === 'otro' && (
                  <div className="mt-3">
                    <Input
                      placeholder="Especifica el canal (ej: Email, Telegram, Evento...)"
                      value={toqueForm.customChannel}
                      onChange={(e) => setToqueForm({ ...toqueForm, customChannel: e.target.value })}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Estado</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setToqueForm({ ...toqueForm, status: 'contactado' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      toqueForm.status === 'contactado' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                    <span className="text-sm font-medium">Contactado</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setToqueForm({ ...toqueForm, status: 'por_contactar' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      toqueForm.status === 'por_contactar' 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Clock className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                    <span className="text-sm font-medium">Por Contactar</span>
                  </button>
                </div>
              </div>
              {/* Attitude Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Actitud del Referido</label>
                <div className="flex justify-between gap-2">
                  {attitudeEmojis.map((attitude) => (
                    <button
                      key={attitude.value}
                      type="button"
                      onClick={() => setToqueForm({ ...toqueForm, attitude: attitude.value })}
                      className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                        toqueForm.attitude === attitude.value
                          ? `${attitude.color} border-slate-400 shadow-sm`
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                      title={attitude.label}
                    >
                      <span className="text-2xl">{attitude.emoji}</span>
                      <span className="text-[10px] text-slate-500 text-center leading-tight">{attitude.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Comentario</label>
                <textarea
                  value={toqueForm.comment}
                  onChange={(e) => setToqueForm({ ...toqueForm, comment: e.target.value })}
                  placeholder="Notas sobre el contacto..."
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={closeToqueModal} className="flex-1">Cancelar</Button>
                <Button 
                  onClick={editingToque ? handleEditToque : handleAddToque} 
                  disabled={savingToque} 
                  className="flex-1 gap-2"
                >
                  {savingToque ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : editingToque ? (
                    <Save className="w-4 h-4" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  {editingToque ? 'Guardar' : 'Agregar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Commission Modal */}
      {showCommissionModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-slate-900">Editar Comisión</h3>
              <button onClick={() => setShowCommissionModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Lead</p>
                <p className="font-medium text-slate-900">{selectedLead.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Monto (COP)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    type="text"
                    value={commissionForm.amount ? commissionForm.amount.toLocaleString('es-CO') : ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setCommissionForm({ ...commissionForm, amount: parseInt(value) || 0 });
                    }}
                    className="pl-7"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Estado</label>
                <select
                  value={commissionForm.status}
                  onChange={(e) => setCommissionForm({ ...commissionForm, status: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">Pendiente</option>
                  <option value="approved">Aprobada</option>
                  <option value="paid">Pagada</option>
                  <option value="rejected">Rechazada</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCommissionModal(false)} className="flex-1">Cancelar</Button>
                <Button onClick={handleUpdateCommission} className="flex-1 gap-2">
                  <CheckCircle className="w-4 h-4" /> Guardar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
